// The assistant panel (Kareem, 4 Nov: "click on a button and a door opens up with the chat and the interface of the
// AI"). ONE panel for every "ask the AI" spot: a drawer from the right on a wide screen (over the page, which stays
// visible behind a soft scrim), a full-height sheet on a phone. It holds the real conversation: messages, cards with
// Approve, the composer with attach and the microphone, and what it is looking at. Each entry point opens it scoped to
// where you are (a space, a track, an activity) with an optional opening question; the conversation is saved like any
// other, and Open full screen takes it to /assistant/:id.
//
// open() from anywhere inside the shell (useAssistantPanel). Opening again on the same scope with no new question
// carries on the same conversation; a question is added to it.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useQueryClient } from '@tanstack/react-query';
import type { ConversationPurpose } from '@dpw/shared';
import { api } from '../api';
import { usePhone } from '../lib/media';
import { scrim, spring, stagger, listItem, sidePanel } from '../lib/motion';
import { useSpaces, useStarters } from '../lib/queries';
import { useSheetFocus } from '../ui/Sheet';
import { IconPlus, IconX } from '../ui/icons';
import { Orb, Skel } from '../ui/motion';
import { useToast } from '../ui/toast';
import { P } from '../phone/kit';
import { useChat } from './useChat';
import { Thread } from './Thread';
import { Composer } from './Composer';
import { VoiceBar, VoiceModeButton } from './VoiceBar';
import { useVoiceMode } from '../voice/useVoiceMode';
import { useDock } from '../shell/Dock';
import { usePageScope, type AiScope } from './AiButton';

export interface PanelOpen {
  spaceId?: string | null; trackId?: string | null; activityId?: string | null;
  /** A Knowledge base folder or file: the assistant answers from its text. */
  folderId?: string | null; fileId?: string | null;
  purpose?: ConversationPurpose | null;
  /** What the scope chip says (the track or activity name) when the panel cannot work it out from the space. */
  label?: string | null;
  /** An opening question: sent at once (send), or put in the box to finish. */
  prompt?: string | null; send?: boolean;
  /** Open this conversation. */
  conversationId?: string | null;
  /** Always start a new conversation. */
  fresh?: boolean;
  /** Start voice mode at once (the opening tap called unlockAudio for iOS). */
  voice?: boolean;
  /** The manual way to do the same thing ("Fill it in myself"): closes the panel and runs it. */
  manual?: { label: string; run: () => void } | null;
}

interface Scope { spaceId: string | null; trackId: string | null; activityId: string | null; folderId: string | null; fileId: string | null; purpose: ConversationPurpose | null; label: string | null }
interface PanelState { scope: Scope; convId: string | null; pending: { text: string; send: boolean; seq: number } | null; manual: PanelOpen['manual']; voice: number; focus?: number }

const NONE: Scope = { spaceId: null, trackId: null, activityId: null, folderId: null, fileId: null, purpose: null, label: null };
const same = (a: Scope, b: Scope) => a.spaceId === b.spaceId && a.trackId === b.trackId && a.activityId === b.activityId && a.folderId === b.folderId && a.fileId === b.fileId && a.purpose === b.purpose;

interface PanelApi {
  open: (o?: PanelOpen) => void; close: () => void; isOpen: boolean;
  /** Quick capture: the same drawer, a new conversation for "what happened", cards to approve in it. */
  capture: () => void;
}
const Ctx = createContext<PanelApi>({ open: () => undefined, close: () => undefined, isOpen: false, capture: () => undefined });
export const useAssistantPanel = () => useContext(Ctx);

let seq = 0;

/** The page's scope as the panel's (Today's Quick capture is just Everything when followed). */
const fromPage = (p: AiScope | null): Scope => ({ ...NONE, spaceId: p?.spaceId ?? null, trackId: p?.trackId ?? null, activityId: p?.activityId ?? null,
  folderId: p?.folderId ?? null, fileId: p?.fileId ?? null, purpose: p?.capture ? null : p?.purpose ?? null, label: p?.label ?? null });

export function AssistantPanelProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [st, setSt] = useState<PanelState>({ scope: NONE, convId: null, pending: null, manual: null, voice: 0 });
  const { pathname } = useLocation();
  // Docked (6.2): on a wide screen the panel sits in the dock and pushes the page; it stays open across pages.
  const dock = useDock();
  // Asked from inside a sheet (a meeting's Ask about it): it opens over that sheet as before, not in the dock under it.
  const [overSheet, setOverSheet] = useState(false);
  const docked = !!dock?.wide && !!dock.aiSlot && !overSheet;
  const setAiOpen = dock?.setAiOpen;
  useEffect(() => { setAiOpen?.(open && !overSheet); if (!open) setOverSheet(false); }, [open, overSheet, setAiOpen]);
  // Each open brings it to the front of the dock (when the dock has Activity | AI tabs).
  const setDockTab = dock?.setTab;
  useEffect(() => { if (open) setDockTab?.('ai'); }, [open, st.focus, setDockTab]);
  // Its scope follows the page unless pinned (opened on something other than the page, or pinned by hand).
  const page = usePageScope();
  const [pinned, setPinned] = useState(false);
  const pageKey = JSON.stringify(fromPage(page));
  const pageNow = useRef(page);
  pageNow.current = page;
  useEffect(() => {
    if (!open || !docked || pinned) return;
    const next = fromPage(pageNow.current);
    setSt(prev => (same(prev.scope, next) ? prev : { scope: next, convId: null, pending: null, manual: null, voice: 0 }));
  }, [pageKey]); // follows the page only when the page changes
  const at = useRef(pathname);
  // Going to another page closes the panel; the conversation is kept. A page the assistant itself opened ("open the
  // steering committee") changes behind the panel, which stays. The page's own address keeping up with it (a search
  // as it is typed, a filter) is not going anywhere, so it stays open for that.
  const keep = useRef(false);
  useEffect(() => {
    if (at.current === pathname) return;
    at.current = pathname;
    // The Assistant page is the assistant itself: the panel gives way to it. Docked, it stays open on other pages.
    if (pathname.startsWith('/assistant')) { keep.current = false; setOpen(false); return; }
    if (keep.current || docked) keep.current = false; else setOpen(false);
  }, [pathname, docked]);
  // A link followed from inside the panel (a source, a meeting moment, Open full screen) closes it, even to a moment
  // of the page behind it (?at=), so you see what it points at.
  useEffect(() => {
    if (!open || docked) return;
    const onClick = (e: MouseEvent) => {
      const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
      if (a && a.closest('.aip') && !e.metaKey && !e.ctrlKey && !e.shiftKey && a.getAttribute('target') !== '_blank') setOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [open, docked]);

  const openPanel = useCallback((o: PanelOpen = {}) => {
    const scope: Scope = { spaceId: o.spaceId ?? null, trackId: o.trackId ?? null, activityId: o.activityId ?? null, folderId: o.folderId ?? null, fileId: o.fileId ?? null, purpose: o.purpose ?? null, label: o.label ?? null };
    // Opened on something other than the page (an activity's panel, a question about another space): pinned there.
    const p = fromPage(pageNow.current);
    setPinned(!same({ ...scope, purpose: scope.purpose === 'capture' ? null : scope.purpose }, p));
    setSt(prev => {
      const keep = !o.fresh && !o.conversationId && prev.convId && same(prev.scope, scope);
      return {
        scope, convId: o.conversationId ?? (keep ? prev.convId : null), manual: o.manual ?? null,
        pending: o.prompt?.trim() ? { text: o.prompt, send: !!o.send, seq: ++seq } : null,
        voice: o.voice ? ++seq : 0, focus: ++seq,
      };
    });
    setOverSheet(!!document.body.dataset.sheets);
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);
  const capture = useCallback(() => openPanel({ purpose: 'capture', fresh: true }), [openPanel]);
  const value = useMemo(() => ({ open: openPanel, close, isOpen: open, capture }), [openPanel, close, open, capture]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {docked && dock?.aiSlot ? (open ? createPortal(
        <AssistantPanel key="aip" st={st} setSt={setSt} onClose={close} docked pinned={pinned} onPin={() => setPinned(x => !x)} keepOpen={() => { keep.current = true; }} />, dock.aiSlot) : null) : (
        <AnimatePresence>
          {open && <AssistantPanel key="aip" st={st} setSt={setSt} onClose={close} keepOpen={() => { keep.current = true; window.setTimeout(() => { keep.current = false; }, 1000); }} />}
        </AnimatePresence>
      )}
    </Ctx.Provider>
  );
}

/** Questions that work as they are, for a panel opened for a purpose (the Research Center's used to sit on its page). */
const TRIES: Partial<Record<ConversationPurpose, string[]>> = {
  research: ['What do we know about clinical AI validation rules?', 'Which scoring models have hospitals used?', 'Find more about sepsis early warning'],
};

const EMPTY: Record<ConversationPurpose | 'none', { h: (where: string | null) => string; p: string }> = {
  none: { h: w => (w ? `Ask about ${w}` : 'What do you need?'), p: 'Ask anything, or tell it what to do: remind, create, file, draft, research. It shows you a card first, and nothing happens until you approve.' },
  insight: { h: () => 'Add an insight', p: 'Paste a link or the text, and say why it matters. The assistant reads it and drafts the insight. Nothing is added until you approve.' },
  research: { h: () => 'Ask the Research Center', p: 'Answers come from the insights the team kept, with their sources. When nothing answers it, the assistant offers to research it.' },
  capture: { h: () => 'What happened?', p: 'Type it, or press the microphone and say it; your words appear as you speak. The assistant works out where each part goes and shows you a card for each, here, to approve. Nothing is filed until you do.' },
  voice: { h: () => 'Voice control', p: 'Say where to go or what to do.' },
};

function AssistantPanel({ st, setSt, onClose, keepOpen, docked = false, pinned = false, onPin }: {
  st: PanelState; setSt: (f: (s: PanelState) => PanelState) => void; onClose: () => void; keepOpen: () => void;
  /** In the dock: no scrim, not modal, Escape only with focus inside; a pin keeps its scope when the page changes. */
  docked?: boolean; pinned?: boolean; onPin?: () => void;
}) {
  const nav = useNavigate();
  const phone = usePhone();
  const reduce = useReducedMotion();
  const qc = useQueryClient();
  const toast = useToast();
  const spaces = useSpaces();
  const panel = useRef<HTMLElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const end = useRef<HTMLDivElement>(null);
  const [text, setText] = useState('');
  const { scope, convId } = st;
  useSheetFocus(panel, onClose, !docked);

  const chat = useChat(convId, {
    key: 'panel',
    create: () => api.assistant.create({ spaceId: scope.spaceId, trackId: scope.trackId, activityId: scope.activityId, folderId: scope.folderId, fileId: scope.fileId, purpose: scope.purpose }),
    onCreated: id => setSt(s => ({ ...s, convId: id })),
    // A page the assistant opened shows behind the panel.
    onReply: r => { if (r.reply.meta.nav) { keepOpen(); nav(r.reply.meta.nav.to); } },
  });
  const { conv, messages, thinking } = chat;
  useEffect(() => { end.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }); }, [messages.length, thinking]);
  useEffect(() => { if (chat.failedText) setText(chat.failedText); }, [chat.failedText]);

  // The opening question: sent once, or put in the box (cursor at the end) to finish.
  const done = useRef(0);
  useEffect(() => {
    const p = st.pending;
    if (!p || done.current === p.seq) return;
    done.current = p.seq;
    if (p.send) void chat.say(p.text);
    else { setText(p.text); window.setTimeout(() => { input.current?.focus(); input.current?.setSelectionRange(p.text.length, p.text.length); }, 0); }
  }, [st.pending, chat]);
  // The box takes the focus when the panel opens (after the focus trap picked its first control).
  // Docked, it can be opened again while open (another page's Ask AI): the box takes the focus again.
  useEffect(() => { const t = window.setTimeout(() => input.current?.focus({ preventScroll: true }), 30); return () => window.clearTimeout(t); }, [st.focus]);

  const say = (t: string) => { if (!t.trim() || chat.busy) return; setText(''); void chat.say(t); };
  // Voice mode in the same chat: your words in a live bubble, the reply read out as it streams in.
  const voice = useVoiceMode((t, o) => chat.say(t, o));
  const started = useRef(0);
  useEffect(() => { if (st.voice && started.current !== st.voice) { started.current = st.voice; voice.start(); } }, [st.voice, voice]);
  const attach = async (files: File[]) => { try { await chat.attach(files); } catch (e) { toast(e instanceof Error ? e.message : 'Could not attach that.'); } };

  // What it is looking at: the conversation's own scope once it exists, else what the panel was opened on.
  const c = conv.data;
  const spaceName = c?.spaceName ?? (scope.spaceId ? (spaces.data ?? []).find(s => s.id === scope.spaceId)?.name ?? null : null);
  const where = c ? (c.fileName ?? c.folderName ?? c.activityTitle ?? c.trackName ?? c.spaceName) : (scope.label ?? spaceName);
  const whereFull = c ? [c.spaceName, c.trackName, c.activityTitle, c.folderName, c.fileName].filter(Boolean).join(' · ')
    : [spaceName, scope.label !== spaceName ? scope.label : null].filter(Boolean).join(' · ');
  const scoped = !!(c ? c.spaceId || c.folderId || c.fileId : scope.spaceId || scope.trackId || scope.activityId || scope.folderId || scope.fileId);
  const widen = async () => {
    if (convId) { try { await api.assistant.setSpace(convId, null); await qc.invalidateQueries({ queryKey: ['conversation', convId] }); } catch (e) { toast(e instanceof Error ? e.message : 'Could not change that.'); return; } }
    setSt(s => ({ ...s, scope: { ...NONE, purpose: s.scope.purpose } }));
  };
  const fresh = () => { setSt(s => ({ ...s, convId: null, pending: null })); setText(''); chat.reset(); window.setTimeout(() => input.current?.focus(), 0); };
  const manual = st.manual ? () => { onClose(); st.manual!.run(); } : null;

  const purpose = c?.purpose ?? scope.purpose;
  const empty = !convId && !chat.busy && !voice.on;
  const waitingHere = chat.actions.some(a => a.status === 'waiting');
  const placeholder = purpose === 'capture' ? (empty ? 'Sara says the room is booked, B2. Remind me Monday 8am to print the matrix.' : 'Add more, or say what to change')
    : purpose === 'insight' && empty ? 'Paste a link or the text, and why it matters'
    : waitingHere ? 'Say approve, or what to change' : where ? `Ask about ${where}` : 'Ask anything';
  const title = c && c.title !== 'New conversation' ? c.title : EMPTY[purpose ?? 'none'].h(null) === 'What do you need?' ? 'Assistant' : EMPTY[purpose ?? 'none'].h(null);
  const full = convId ? <Link className={phone ? 'ph-nb' : 'ib'} to={`/assistant/${convId}`} aria-label="Open full screen" title="Open full screen"><IconExpand /></Link> : null;

  const chip = (
    <div className="aip-scope">
      <span className="k">Looking at</span>
      <span className={`aip-chip${scoped ? ' on' : ''}`} title={whereFull || 'Everything'}>
        <span className="t">{whereFull || 'Everything'}</span>
        {scoped && <button type="button" className="x" aria-label="Look at everything instead" onClick={() => void widen()}><IconX /></button>}
      </span>
      {manual && !empty && <button type="button" className="aip-manual" onClick={manual}>{st.manual!.label}</button>}
    </div>
  );
  const body = (
    <>
      {empty && <PanelEmpty purpose={purpose} where={where} spaceId={c?.spaceId ?? scope.spaceId} manual={manual ? { label: st.manual!.label, run: manual } : null}
        onPick={(t, complete) => { if (complete) say(t); else { setText(t); window.setTimeout(() => { input.current?.focus(); input.current?.setSelectionRange(t.length, t.length); }, 0); } }} />}
      <Thread messages={messages} actions={chat.actions} thinking={thinking} reply={chat.liveReply} loading={!!convId && conv.isLoading} error={chat.error} onAsk={say} phone={phone} live={voice.on ? voice.heard : null} />
      <div ref={end} />
    </>
  );
  const composer = voice.on ? (
    <div className={phone ? 'ph-foot aip-compose' : 'compose aip-compose'}><VoiceBar voice={voice} phone={phone} /></div>
  ) : (
    <Composer extra={<VoiceModeButton voice={voice} phone={phone} disabled={thinking} />} value={text} onChange={setText} onSend={say} onFiles={f => void attach(f)} staged={chat.staged} thinking={thinking}
      placeholder={placeholder} phone={phone} inputRef={input} className="aip-compose"
      stagedNote={<span className="xs mut">Say where it should go.</span>} />
  );

  if (docked) {
    return (
      <motion.section ref={panel} className="aip docked" role="dialog" aria-modal="false" aria-label="Assistant"
        initial={false}
        onKeyDown={e => { if (e.key === 'Escape' && !e.defaultPrevented) { e.stopPropagation(); onClose(); } }}>
        <header className="aip-h">
          <span className="aip-t"><Orb size={16} state={thinking ? 'thinking' : 'idle'} /><b>{title}</b></span>
          <button type="button" className={`ib pin${pinned ? ' on' : ''}`} aria-pressed={pinned} aria-label={pinned ? 'Pinned to this scope: follow the page instead' : 'Pin this scope (it follows the page now)'}
            title={pinned ? 'Pinned: stays on this scope when you change page' : 'Follows the page. Pin to keep this scope'} onClick={onPin}><IconPin /></button>
          {full}
          <button type="button" className="ib" aria-label="New conversation" title="New conversation" onClick={fresh}><IconPlus /></button>
          <button type="button" className="ib" aria-label="Close" onClick={onClose}><IconX /></button>
        </header>
        {chip}
        <div className="aip-body"><div className="thread aip-thread">{body}</div></div>
        {composer}
      </motion.section>
    );
  }
  if (phone) {
    // Inside a .ph host of its own, so the phone's styles apply (the host itself is see-through).
    return (
      <div className="ph aip-host">
      <motion.section ref={panel} className="aip phone ph-frame" role="dialog" aria-modal="true" aria-label="Assistant"
        initial={reduce ? { opacity: 0 } : { y: '100%' }} animate={reduce ? { opacity: 1 } : { y: 0 }} exit={reduce ? { opacity: 0 } : { y: '100%' }} transition={spring.gentle}>
        <div className="ph-tbar aip-top">
          <button type="button" className="ph-nb" aria-label="Close" onClick={onClose}><P.x /></button>
          <span className="aip-pt"><Orb size={14} state={thinking ? 'thinking' : 'idle'} /><b>{title}</b></span>
          {full}
          <button type="button" className="ph-nb" aria-label="New conversation" onClick={fresh}><P.compose /></button>
        </div>
        {chip}
        <div className="ph-body"><div className="ph-thread">{body}</div></div>
        {composer}
      </motion.section>
      </div>
    );
  }
  return (
    <>
      <motion.button type="button" className="scrim aip-scrim" onClick={onClose} aria-label="Close the assistant" tabIndex={-1}
        variants={scrim} initial="initial" animate="animate" exit="exit" />
      <motion.aside ref={panel} className="aip" role="dialog" aria-modal="true" aria-label="Assistant"
        initial={reduce ? { opacity: 0 } : { x: '100%' }} animate={reduce ? { opacity: 1 } : { x: 0 }} exit={reduce ? { opacity: 0 } : { x: '100%', transition: sidePanel.close }}
        transition={reduce ? spring.gentle : sidePanel.open}>
        <header className="aip-h">
          <span className="aip-t"><Orb size={16} state={thinking ? 'thinking' : 'idle'} /><b>{title}</b></span>
          {full}
          <button type="button" className="ib" aria-label="New conversation" title="New conversation" onClick={fresh}><IconPlus /></button>
          <button type="button" className="ib" aria-label="Close" onClick={onClose}><IconX /></button>
        </header>
        {chip}
        <div className="aip-body"><div className="thread aip-thread">{body}</div></div>
        {composer}
      </motion.aside>
    </>
  );
}

function PanelEmpty({ purpose, where, spaceId, manual, onPick }: {
  purpose: ConversationPurpose | null; where: string | null; spaceId: string | null; manual: { label: string; run: () => void } | null;
  onPick: (t: string, complete: boolean) => void;
}) {
  const e = EMPTY[purpose ?? 'none'];
  const starters = useStarters(spaceId ?? undefined);
  const showStarters = !purpose;
  const tries = purpose ? TRIES[purpose] : undefined;
  return (
    <div className="aip-empty">
      <span className="aip-orb"><Orb size={40} /></span>
      <h2>{e.h(where)}</h2>
      <p>{e.p}</p>
      {manual && <button type="button" className="bt" onClick={manual.run}>{manual.label}</button>}
      {tries && (
        <div className="aip-try">
          <span className="k">Try</span>
          <div className="stl">{tries.map(t => <button key={t} type="button" className="stc" onClick={() => onPick(t, true)}><span>{t}</span></button>)}</div>
        </div>
      )}
      {showStarters && (
        <div className="aip-try">
          <span className="k">Try</span>
          {starters.isLoading ? [0, 1, 2].map(i => <div className="stc" key={i}><Skel w={`${60 + i * 10}%`} h={13} /></div>) : (
            <motion.div className="stl" variants={{ animate: { transition: stagger(0.04, 0.04) } }} initial="initial" animate="animate">
              {(starters.data ?? []).slice(0, 4).map(s => (
                <motion.button key={s.text} type="button" className="stc" variants={listItem} whileTap={{ scale: 0.99 }} onClick={() => onPick(s.text, s.complete)}>
                  <span>{s.text}{!s.complete && <span className="mut">...</span>}</span><span className="xs mut">{s.hint}</span>
                </motion.button>
              ))}
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
}

const IconExpand = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" /></svg>;
const IconPin = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 4h6l-1 6 4 4H6l4-4zM12 14v7" /></svg>;
